import React, { useState, useEffect } from 'react';
import {
  EmailAuthProvider,
  reauthenticateWithCredential,
  updatePassword,
  signOut,
} from 'firebase/auth';
import { useConfirm } from './ConfirmDialog';
import MFAEinstellungen from './MFAEinstellungen';
import { abrechnungAbrufen, getNutzerIdentitaet, setNutzerIdentitaet, checkoutSessionErstellen, aboKuendigen, deleteAccount, emailAenderungAnfordern } from '../api';
import PricingModal from './PricingModal';
import { auth } from '../firebase';
import { capture } from '../tracking/posthog';
import './ProfilEinstellungen.css';

// ── Icons ────────────────────────────────────────────────────────

const IdentitaetIcon = ({ size = 24, color = '#000000' }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <g clipPath="url(#clip_ident)">
      <path d="M17 21H7C3 21 2 20 2 16V8C2 4 3 3 7 3H17C21 3 22 4 22 8V16C22 20 21 21 17 21Z" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
      <path d="M14 8H19" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
      <path d="M15 12H19" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
      <path d="M17 16H19" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
      <path d="M8.49994 11.2899C9.49958 11.2899 10.3099 10.4796 10.3099 9.47992C10.3099 8.48029 9.49958 7.66992 8.49994 7.66992C7.50031 7.66992 6.68994 8.48029 6.68994 9.47992C6.68994 10.4796 7.50031 11.2899 8.49994 11.2899Z" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
      <path d="M12 16.3298C11.86 14.8798 10.71 13.7398 9.26 13.6098C8.76 13.5598 8.25 13.5598 7.74 13.6098C6.29 13.7498 5.14 14.8798 5 16.3298" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
    </g>
    <defs>
      <clipPath id="clip_ident"><rect width="24" height="24" fill="white" /></clipPath>
    </defs>
  </svg>
);

const AccountIcon = ({ size = 24, color = '#000000' }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <g clipPath="url(#clip_account)">
      <path d="M6 10V8C6 4.69 7 2 12 2C17 2 18 4.69 18 8V10" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
      <path d="M17 22H7C3 22 2 21 2 17V15C2 11 3 10 7 10H17C21 10 22 11 22 15V17C22 21 21 22 17 22Z" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
      <path d="M15.9965 16H16.0054" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
      <path d="M11.9955 16H12.0045" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
      <path d="M7.99451 16H8.00349" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
    </g>
    <defs>
      <clipPath id="clip_account"><rect width="24" height="24" fill="white" /></clipPath>
    </defs>
  </svg>
);

const MFAIcon = ({ size = 24, color = '#000000' }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <g clipPath="url(#clip_mfa)">
      <path d="M20.91 11.1203C20.91 16.0103 17.36 20.5903 12.51 21.9303C12.18 22.0203 11.82 22.0203 11.49 21.9303C6.63996 20.5903 3.08997 16.0103 3.08997 11.1203V6.73028C3.08997 5.91028 3.70998 4.98028 4.47998 4.67028L10.05 2.39031C11.3 1.88031 12.71 1.88031 13.96 2.39031L19.53 4.67028C20.29 4.98028 20.92 5.91028 20.92 6.73028L20.91 11.1203Z" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
      <path d="M12 12.5C13.1046 12.5 14 11.6046 14 10.5C14 9.39543 13.1046 8.5 12 8.5C10.8954 8.5 10 9.39543 10 10.5C10 11.6046 10.8954 12.5 12 12.5Z" stroke={color} strokeWidth="1.5" strokeMiterlimit="10" strokeLinecap="round" strokeLinejoin="round" />
      <path d="M12 12.5V15.5" stroke={color} strokeWidth="1.5" strokeMiterlimit="10" strokeLinecap="round" strokeLinejoin="round" />
    </g>
    <defs>
      <clipPath id="clip_mfa"><rect width="24" height="24" fill="white" /></clipPath>
    </defs>
  </svg>
);

const AugeIcon = ({ size = 18, color = 'currentColor', offen = false }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    {offen ? (
      <>
        <path d="M2 2L22 22" stroke={color} strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" />
        <path d="M9.88 9.88C9.34 10.42 9 11.17 9 12C9 13.66 10.34 15 12 15C12.83 15 13.58 14.66 14.12 14.12" stroke={color} strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" />
        <path d="M6.1 6.44C4.38 7.6 3.01 9.31 2.05 11.5C1.98 11.66 1.98 11.84 2.05 12C3.64 15.62 7.06 18 12 18C13.45 18 14.76 17.8 15.92 17.41" stroke={color} strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" />
        <path d="M19.33 15.12C20.43 14.18 21.31 12.99 21.95 11.5C22.02 11.34 22.02 11.16 21.95 11C20.36 7.38 16.94 5 12 5C11.31 5 10.65 5.05 10.03 5.14" stroke={color} strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" />
      </>
    ) : (
      <>
        <path d="M2.05 11.5C3.64 7.88 7.06 5.5 12 5.5C16.94 5.5 20.36 7.88 21.95 11.5C22.02 11.66 22.02 11.84 21.95 12C20.36 15.62 16.94 18 12 18C7.06 18 3.64 15.62 2.05 12C1.98 11.84 1.98 11.66 2.05 11.5Z" stroke={color} strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" />
        <path d="M12 15C13.66 15 15 13.66 15 12C15 10.34 13.66 9 12 9C10.34 9 9 10.34 9 12C9 13.66 10.34 15 12 15Z" stroke={color} strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" />
      </>
    )}
  </svg>
);


const AbrechnungIcon = ({ size = 24, color = '#000000' }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <g clipPath="url(#clip_abr)">
      <path d="M22 7.81V12.9C22 17 20 19 15.9 19H8.1C4 19 2 17 2 12.9V7.81C2 4.17 3.64 2.29 6.9 2.04C7.26 2.01 7.66 2 8.1 2H15.9C16.34 2 16.74 2.01 17.1 2.04C20.36 2.29 22 4.17 22 7.81Z" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
      <path d="M7 9.5H17" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
      <path d="M7 14.5H13" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
    </g>
    <defs>
      <clipPath id="clip_abr"><rect width="24" height="24" fill="white" /></clipPath>
    </defs>
  </svg>
);

// ── Tab-Konfiguration ─────────────────────────────────────────────

const TABS = [
  { id: 'identitaet', label: 'Identität',            Icon: IdentitaetIcon },
  { id: 'account',    label: 'Anmeldung & Sicherheit', Icon: AccountIcon },
  { id: 'abrechnung', label: 'Abrechnung',             Icon: AbrechnungIcon },
];

// ── Haupt-Komponente ─────────────────────────────────────────────

export default function ProfilEinstellungen({ nutzer }) {
  const confirm = useConfirm();
  const [aktiveTab, setAktiveTab] = useState('identitaet');
  const [abrechnung, setAbrechnung] = useState(null);
  const [abrLaden, setAbrLaden] = useState(true);
  const [preisModalOffen, setPreisModalOffen] = useState(false);
  const [kauftGrade, setKauftGrade] = useState(false);

  const kaufen = async (preisId, ts) => {
    capture('checkout_started', { plan: preisId });
    setKauftGrade(true);
    try {
      const r = await checkoutSessionErstellen(preisId, ts);
      window.location.href = r.data.checkout_url;
    } catch {
      setKauftGrade(false);
    }
  };

  const abrechnungLaden = () => {
    setAbrLaden(true);
    abrechnungAbrufen()
      .then(r => setAbrechnung(r.data))
      .catch(() => setAbrechnung(null))
      .finally(() => setAbrLaden(false));
  };

  const kuendigen = async () => {
    const ok = await confirm('Abo wirklich kündigen? Du behältst Zugriff bis zum Ende der aktuellen Laufzeit.');
    if (!ok) return;
    try {
      await aboKuendigen();
      abrechnungLaden();
    } catch {
      // silently reload to show current state
      abrechnungLaden();
    }
  };

  useEffect(() => { abrechnungLaden(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className="pe-wrapper">
      {/* Header */}
      <div className="pe-header">
        <h1 className="pe-header-titel">Profil Einstellungen</h1>
      </div>

      {/* Body */}
      <div className="pe-body">
        {/* Vertikale Tab-Leiste */}
        <nav className="pe-sidebar">
          {TABS.map(({ id, label, Icon }) => (
            <button
              key={id}
              className={`pe-tab-btn${aktiveTab === id ? ' pe-tab-btn--aktiv' : ''}`}
              onClick={() => setAktiveTab(id)}
            >
              <span className="pe-tab-icon">
                <Icon
                  size={20}
                  color={aktiveTab === id ? 'var(--clr-primary)' : 'var(--txt-muted)'}
                />
              </span>
              <span className="pe-tab-label">{label}</span>
            </button>
          ))}
          <button
            className="pe-logout-btn"
            onClick={async () => { if (await confirm('Wirklich abmelden?')) signOut(auth); }}
          >
            Abmelden
          </button>
        </nav>

        {/* Inhalt */}
        <div className="pe-inhalt">
          {aktiveTab === 'identitaet' && <IdentitaetTab nutzer={nutzer} />}
          {aktiveTab === 'account' && <AccountTab nutzer={nutzer} />}
          {aktiveTab === 'abrechnung' && (
            <AbrechnungTab
              abrechnung={abrechnung}
              laden={abrLaden}
              onPaketWaehlen={() => setPreisModalOffen(true)}
              onRefresh={abrechnungLaden}
              onKuendigen={kuendigen}
            />
          )}
        </div>
      </div>

      <PricingModal
        offen={preisModalOffen}
        onSchliessen={() => setPreisModalOffen(false)}
        onKaufen={kaufen}
        kauftGrade={kauftGrade}
      />

    </div>
  );
}

// ── Tab: Identität ───────────────────────────────────────────────

function IdentitaetTab({ nutzer }) {
  const [form, setForm] = useState({ vorname: '', nachname: '', nickname: '' });
  const [gespeichert, setGespeichert] = useState(false);
  const [laden, setLaden] = useState(true);
  const [fehler, setFehler] = useState('');

  const fallbackName = nutzer?.displayName || '';
  const fallbackVorname = fallbackName.split(' ')[0] || '';
  const fallbackNachname = fallbackName.split(' ').slice(1).join(' ') || '';

  useEffect(() => {
    getNutzerIdentitaet()
      .then(res => {
        const next = {
          vorname: res.data.vorname || fallbackVorname,
          nachname: res.data.nachname || fallbackNachname,
          nickname: res.data.nickname || '',
        };
        setForm(next);
      })
      .catch(() => {})
      .finally(() => setLaden(false));
  }, [fallbackVorname, fallbackNachname]);

  const speichern = async () => {
    setFehler('');
    try {
      const payload = {
        vorname: form.vorname.trim(),
        nachname: form.nachname.trim(),
        nickname: form.nickname.trim(),
      };
      const res = await setNutzerIdentitaet(payload);
      setForm({
        vorname: res.data.vorname || '',
        nachname: res.data.nachname || '',
        nickname: res.data.nickname || '',
      });
      setGespeichert(true);
      setTimeout(() => setGespeichert(false), 2500);
    } catch {
      setFehler('Speichern fehlgeschlagen. Bitte erneut versuchen.');
    }
  };

  return (
    <div className="pe-section">
      <div className="pe-section-header">
        <div>
          <h2>Identität</h2>
        </div>
      </div>

      <div className="pe-untersektion">
        <h3 className="pe-untersektion-titel">Spitzname</h3>
        <p className="pe-hinweis" style={{ marginBottom: '12px' }}>
          Wie soll der KI-Assistent dich ansprechen? Wird auf dem Dashboard als Begrüßung verwendet.
        </p>
        <div className="pe-felder-grid">
          <div className="pe-feld pe-feld--full">
            <label className="pe-feld-label">Spitzname</label>
            <input
              className="pe-input"
              value={laden ? '' : form.nickname}
              onChange={e => setForm(prev => ({ ...prev, nickname: e.target.value }))}
              placeholder=""
              disabled={laden}
              maxLength={30}
            />
          </div>
        </div>
        {fehler && <p style={{ color: 'var(--clr-danger, #dc2626)', fontSize: '0.85rem', marginTop: '6px' }}>{fehler}</p>}
      </div>

      <div style={{ marginTop: 'auto', display: 'flex', justifyContent: 'flex-end' }}>
        <button
          className="pe-btn-speichern"
          onClick={speichern}
          disabled={laden}
        >
          {gespeichert ? 'Gespeichert ✓' : 'Speichern'}
        </button>
      </div>
    </div>
  );
}

// ── Tab: Anmeldung & Sicherheit ──────────────────────────────────

function AccountTab({ nutzer }) {
  const passwordProviderAktiv = nutzer?.providerData?.some(p => p.providerId === 'password');
  const [emailForm, setEmailForm] = useState({ neueEmail: '', passwort: '' });
  const [emailStatus, setEmailStatus] = useState({ typ: '', text: '' });
  const [emailLaedt, setEmailLaedt] = useState(false);
  const [emailAnzeigen, setEmailAnzeigen] = useState(false);
  const [emailFormOffen, setEmailFormOffen] = useState(false);
  const [loeschenLaedt, setLoeschenLaedt] = useState(false);
  const [passwortFormOffen, setPasswortFormOffen] = useState(false);
  const [passwortForm, setPasswortForm] = useState({
    aktuellesPasswort: '',
    neuesPasswort: '',
    neuesPasswortBestaetigen: '',
  });
  const [passwortStatus, setPasswortStatus] = useState({ typ: '', text: '' });
  const [passwortLaedt, setPasswortLaedt] = useState(false);

  const confirm = useConfirm();

  async function handleKontoLoeschen() {
    if (loeschenLaedt) return;
    const ersteBestaetigung = await confirm(
      'Konto dauerhaft löschen? Dein Konto, alle Bewerbungen, Dokumente, Einstellungen und hochgeladene Dateien werden unwiderruflich entfernt.'
    );
    if (!ersteBestaetigung) return;
    const zweiteBestaetigung = await confirm(
      'Wirklich löschen? Diese Aktion kann nicht rückgängig gemacht werden. Dein Konto wird sofort und dauerhaft gelöscht.',
      { holdMs: 3000 }
    );
    if (!zweiteBestaetigung) return;
    setLoeschenLaedt(true);
    try {
      await deleteAccount();
      await signOut(auth);
    } catch (err) {
      setLoeschenLaedt(false);
      alert('Konto konnte nicht gelöscht werden. Bitte versuche es erneut oder kontaktiere den Support.');
    }
  }

  const aktuelleEmail = nutzer?.email || '';
  const maskierteEmail = aktuelleEmail
    ? `${aktuelleEmail.slice(0, 3)}${'•'.repeat(Math.max(8, aktuelleEmail.length - 3))}`
    : '';
  const neueEmail = emailForm.neueEmail.trim();
  const kannEmailAendern =
    passwordProviderAktiv &&
    neueEmail &&
    emailForm.passwort &&
    neueEmail.toLowerCase() !== aktuelleEmail.toLowerCase() &&
    !emailLaedt;
  const neuesPasswort = passwortForm.neuesPasswort.trim();
  const passwortStimmtUeberein =
    passwortForm.neuesPasswort === passwortForm.neuesPasswortBestaetigen;
  const kannPasswortAendern =
    passwordProviderAktiv &&
    passwortForm.aktuellesPasswort &&
    neuesPasswort.length >= 8 &&
    passwortStimmtUeberein &&
    !passwortLaedt;

  function setEmailFeld(feld, value) {
    setEmailForm(prev => ({ ...prev, [feld]: value }));
    setEmailStatus({ typ: '', text: '' });
  }

  function emailFormUmschalten() {
    setEmailFormOffen(prev => !prev);
    setEmailForm({ neueEmail: '', passwort: '' });
    setEmailStatus({ typ: '', text: '' });
  }

  function setPasswortFeld(feld, value) {
    setPasswortForm(prev => ({ ...prev, [feld]: value }));
    setPasswortStatus({ typ: '', text: '' });
  }

  function passwortFormUmschalten() {
    setPasswortFormOffen(prev => !prev);
    setPasswortForm({
      aktuellesPasswort: '',
      neuesPasswort: '',
      neuesPasswortBestaetigen: '',
    });
    setPasswortStatus({ typ: '', text: '' });
  }

  function firebaseEmailFehler(error) {
    const code = error?.code || '';
    if (code === 'auth/wrong-password' || code === 'auth/invalid-credential') {
      return 'Das Passwort stimmt nicht. Bitte prüfe deine Eingabe.';
    }
    if (code === 'auth/email-already-in-use') {
      return 'Diese E-Mail-Adresse wird bereits für ein anderes Konto verwendet.';
    }
    if (code === 'auth/invalid-email') {
      return 'Bitte gib eine gültige E-Mail-Adresse ein.';
    }
    if (code === 'auth/requires-recent-login') {
      return 'Bitte melde dich neu an und versuche es direkt danach erneut.';
    }
    if (code === 'auth/too-many-requests') {
      return 'Zu viele Versuche. Bitte warte kurz und versuche es später erneut.';
    }
    return 'Die E-Mail konnte nicht geändert werden. Bitte versuche es erneut.';
  }

  function firebasePasswortFehler(error) {
    const code = error?.code || '';
    if (code === 'auth/wrong-password' || code === 'auth/invalid-credential') {
      return 'Das aktuelle Passwort stimmt nicht. Bitte prüfe deine Eingabe.';
    }
    if (code === 'auth/weak-password') {
      return 'Das neue Passwort ist zu schwach. Bitte verwende mindestens 8 Zeichen.';
    }
    if (code === 'auth/requires-recent-login') {
      return 'Bitte melde dich neu an und versuche es direkt danach erneut.';
    }
    if (code === 'auth/too-many-requests') {
      return 'Zu viele Versuche. Bitte warte kurz und versuche es später erneut.';
    }
    return 'Das Passwort konnte nicht geändert werden. Bitte versuche es erneut.';
  }

  async function emailAenderungStarten() {
    if (!kannEmailAendern || !auth.currentUser || !aktuelleEmail) return;
    setEmailLaedt(true);
    setEmailStatus({ typ: '', text: '' });

    try {
      const credential = EmailAuthProvider.credential(aktuelleEmail, emailForm.passwort);
      await reauthenticateWithCredential(auth.currentUser, credential);
      await emailAenderungAnfordern(neueEmail);
      setEmailForm({ neueEmail: '', passwort: '' });
      setEmailStatus({
        typ: 'erfolg',
        text: 'Bestätigungslink gesendet. Die neue E-Mail wird erst aktiv, nachdem du sie bestätigt hast.',
      });
    } catch (error) {
      setEmailStatus({ typ: 'fehler', text: firebaseEmailFehler(error) });
    } finally {
      setEmailLaedt(false);
    }
  }

  async function passwortAendern() {
    if (!passwordProviderAktiv || !auth.currentUser || !aktuelleEmail) return;
    if (!passwortStimmtUeberein) {
      setPasswortStatus({ typ: 'fehler', text: 'Die neuen Passwörter stimmen nicht überein.' });
      return;
    }
    if (neuesPasswort.length < 8) {
      setPasswortStatus({ typ: 'fehler', text: 'Das neue Passwort muss mindestens 8 Zeichen lang sein.' });
      return;
    }

    setPasswortLaedt(true);
    setPasswortStatus({ typ: '', text: '' });

    try {
      const credential = EmailAuthProvider.credential(aktuelleEmail, passwortForm.aktuellesPasswort);
      await reauthenticateWithCredential(auth.currentUser, credential);
      await updatePassword(auth.currentUser, passwortForm.neuesPasswort);
      setPasswortForm({
        aktuellesPasswort: '',
        neuesPasswort: '',
        neuesPasswortBestaetigen: '',
      });
      setPasswortStatus({ typ: 'erfolg', text: 'Passwort geändert. Nutze ab jetzt dein neues Passwort.' });
      setPasswortFormOffen(false);
    } catch (error) {
      setPasswortStatus({ typ: 'fehler', text: firebasePasswortFehler(error) });
    } finally {
      setPasswortLaedt(false);
    }
  }

  return (
    <div className="pe-section">
      <div className="pe-section-header">
        <div>
          <h2>Anmeldung & Sicherheit</h2>
          <p>E-Mail, Passwort und Zwei-Faktor-Authentifizierung</p>
        </div>
      </div>

      {/* Kontaktdaten */}
      <div className="pe-felder-grid">
        <div className="pe-feld pe-feld--full">
          <label className="pe-feld-label">E-Mail-Adresse</label>
          <div className="pe-email-zeile">
            <input
              className="pe-input pe-input--readonly pe-email-input"
              value={emailAnzeigen ? aktuelleEmail : maskierteEmail}
              readOnly
            />
            <button
              type="button"
              className="pe-icon-btn"
              onClick={() => setEmailAnzeigen(prev => !prev)}
              title={emailAnzeigen ? 'E-Mail verbergen' : 'E-Mail anzeigen'}
              aria-label={emailAnzeigen ? 'E-Mail verbergen' : 'E-Mail anzeigen'}
            >
              <AugeIcon offen={emailAnzeigen} />
            </button>
            {passwordProviderAktiv && (
              <button
                type="button"
                className={`pe-btn-ghost pe-btn-ghost--compact${emailFormOffen ? ' pe-btn-ghost--aktiv' : ''}`}
                onClick={emailFormUmschalten}
              >
                {emailFormOffen ? 'Abbrechen' : 'Ändern'}
              </button>
            )}
          </div>
        </div>

        {passwordProviderAktiv && emailFormOffen ? (
          <div className="pe-feld pe-feld--full">
            <div className="pe-inline-form">
              <h3 className="pe-untersektion-titel">E-Mail ändern</h3>
              <p className="pe-hinweis">
                Aus Sicherheitsgründen brauchst du dein aktuelles Passwort. Die neue Adresse wird erst übernommen,
                nachdem du den Bestätigungslink in der neuen Mailbox geöffnet hast.
              </p>
              <div className="pe-felder-grid pe-felder-grid--einspaltig">
                <div className="pe-feld pe-feld--full">
                  <label className="pe-feld-label">Neue E-Mail-Adresse</label>
                  <input
                    className="pe-input"
                    type="email"
                    value={emailForm.neueEmail}
                    onChange={e => setEmailFeld('neueEmail', e.target.value)}
                    placeholder="neue-adresse@example.com"
                    autoComplete="email"
                  />
                </div>
                <div className="pe-feld pe-feld--full">
                  <label className="pe-feld-label">Aktuelles Passwort</label>
                  <input
                    className="pe-input"
                    type="password"
                    value={emailForm.passwort}
                    onChange={e => setEmailFeld('passwort', e.target.value)}
                    placeholder="Passwort bestätigen"
                    autoComplete="current-password"
                  />
                </div>
              </div>
              {emailStatus.text && (
                <p className={`pe-status pe-status--${emailStatus.typ}`}>
                  {emailStatus.text}
                </p>
              )}
              <div>
                <button
                  className="pe-btn-speichern"
                  onClick={emailAenderungStarten}
                  disabled={!kannEmailAendern}
                >
                  {emailLaedt ? 'Wird gesendet...' : 'Bestätigungslink senden'}
                </button>
              </div>
            </div>
          </div>
        ) : !passwordProviderAktiv ? (
          <div className="pe-feld pe-feld--full">
            <div className="pe-hinweis">
              Deine E-Mail-Adresse wird von deinem Anmeldeanbieter verwaltet. Bei Google- oder LinkedIn-Login
              kann sie deshalb nicht in CVFlow geändert werden.
            </div>
          </div>
        ) : null}

      </div>

      {/* Passwort */}
      <div className="pe-untersektion">
        <h3 className="pe-untersektion-titel">Passwort</h3>
        {passwordProviderAktiv ? (
          <>
            <div className="pe-passwort-karte">
              <div className="pe-passwort-info">
                <span className="pe-passwort-dots">••••••••••••</span>
                <span className="pe-passwort-hint">Nur mit aktuellem Passwort änderbar</span>
              </div>
              <button
                type="button"
                className={`pe-btn-ghost${passwortFormOffen ? ' pe-btn-ghost--aktiv' : ''}`}
                onClick={passwortFormUmschalten}
              >
                {passwortFormOffen ? 'Abbrechen' : 'Ändern'}
              </button>
            </div>

            {passwortFormOffen && (
              <div className="pe-inline-form">
                <p className="pe-hinweis">
                  Gib zuerst dein aktuelles Passwort ein. Danach kannst du ein neues Passwort setzen.
                </p>
                <div className="pe-felder-grid pe-felder-grid--einspaltig">
                  <div className="pe-feld pe-feld--full">
                    <label className="pe-feld-label">Aktuelles Passwort</label>
                    <input
                      className="pe-input"
                      type="password"
                      value={passwortForm.aktuellesPasswort}
                      onChange={e => setPasswortFeld('aktuellesPasswort', e.target.value)}
                      placeholder="Aktuelles Passwort"
                      autoComplete="current-password"
                    />
                  </div>
                  <div className="pe-feld pe-feld--full">
                    <label className="pe-feld-label">Neues Passwort</label>
                    <input
                      className="pe-input"
                      type="password"
                      value={passwortForm.neuesPasswort}
                      onChange={e => setPasswortFeld('neuesPasswort', e.target.value)}
                      placeholder="Mindestens 8 Zeichen"
                      autoComplete="new-password"
                    />
                  </div>
                  <div className="pe-feld pe-feld--full">
                    <label className="pe-feld-label">Neues Passwort wiederholen</label>
                    <input
                      className="pe-input"
                      type="password"
                      value={passwortForm.neuesPasswortBestaetigen}
                      onChange={e => setPasswortFeld('neuesPasswortBestaetigen', e.target.value)}
                      placeholder="Neues Passwort bestätigen"
                      autoComplete="new-password"
                    />
                  </div>
                </div>
                {!passwortStimmtUeberein && passwortForm.neuesPasswortBestaetigen && (
                  <p className="pe-status pe-status--fehler">Die neuen Passwörter stimmen nicht überein.</p>
                )}
                {passwortStatus.text && (
                  <p className={`pe-status pe-status--${passwortStatus.typ}`}>
                    {passwortStatus.text}
                  </p>
                )}
                <div>
                  <button
                    className="pe-btn-speichern"
                    onClick={passwortAendern}
                    disabled={!kannPasswortAendern}
                  >
                    {passwortLaedt ? 'Wird geändert...' : 'Passwort speichern'}
                  </button>
                </div>
              </div>
            )}
            {!passwortFormOffen && passwortStatus.text && (
              <p className={`pe-status pe-status--${passwortStatus.typ}`}>
                {passwortStatus.text}
              </p>
            )}
          </>
        ) : (
          <div className="pe-hinweis">
            Dein Passwort wird von deinem Anmeldeanbieter verwaltet. Bei Google- oder LinkedIn-Login
            kann es deshalb nicht in CVFlow geändert werden.
          </div>
        )}
      </div>

      {/* MFA */}
      <div className="pe-untersektion">
        <div className="pe-untersektion-kopf">
          <MFAIcon size={18} color="var(--clr-secondary)" />
          <h3 className="pe-untersektion-titel">Zwei-Faktor-Authentifizierung</h3>
        </div>
        {nutzer ? (
          <MFAEinstellungen nutzer={nutzer} />
        ) : (
          <p className="pe-hinweis">Anmeldung erforderlich.</p>
        )}
      </div>

      {/* Konto löschen */}
      <div className="pe-untersektion pe-untersektion--gefahr">
        <h3 className="pe-untersektion-titel pe-untersektion-titel--gefahr">Konto löschen</h3>
        <div className="pe-gefahr-panel">
          <p className="pe-gefahr-text">
            Löscht dein Konto sowie alle Bewerbungen, Dokumente, Einstellungen und hochgeladenen Dateien dauerhaft. Diese Aktion ist unwiderruflich.
          </p>
          <button
            type="button"
            className="pe-btn-gefahr"
            onClick={handleKontoLoeschen}
            disabled={loeschenLaedt}
          >
            {loeschenLaedt ? 'Konto wird gelöscht…' : 'Konto dauerhaft löschen'}
          </button>
        </div>
        <p className="pe-datenschutz-hinweis">
          CVFlow speichert deine Daten sicher und gibt sie nicht an Dritte weiter. Du kannst dein Konto und alle zugehörigen Daten jederzeit löschen.
        </p>
      </div>
    </div>
  );
}

// ── Tab: Abrechnung ──────────────────────────────────────────────

function AbrechnungTab({ abrechnung, laden, onPaketWaehlen, onRefresh, onKuendigen }) {
  if (laden) {
    return (
      <div className="pe-section">
        <div className="pe-abr-skeleton" />
        <div className="pe-abr-skeleton pe-abr-skeleton--sm" />
      </div>
    );
  }

  const sub = abrechnung?.subscription ?? null;
  const aboAktiv = abrechnung?.abo_aktiv ?? false;
  const freeLimit = abrechnung?.free_limit ?? 3;
  const freeUebrig = abrechnung?.free_uebrig ?? freeLimit;
  const ablaufFormatiert = sub?.expires_at
    ? new Date(sub.expires_at).toLocaleDateString('de-DE', { day: '2-digit', month: 'long', year: 'numeric' })
    : null;
  const planName = sub?.product_name || 'CVFlow Pro';
  const istAdminGrant = sub?.source === 'admin';
  const istAppleAbo = sub?.source === 'apple';
  const istGekuendigt = sub?.cancel_at_period_end === true;
  const quelleLabel = istAppleAbo
    ? 'Apple App Store'
    : istAdminGrant
    ? 'CVFlow (manuell freigeschaltet)'
    : 'CVFlow-Website (Stripe)';

  return (
    <div className="pe-section">
      <div className="pe-section-header">
        <div>
          <h2>Abrechnung</h2>
          <p>Dein aktives Abonnement</p>
        </div>
        <button className="pe-abr-refresh" onClick={onRefresh} title="Aktualisieren">
          <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            <polyline points="23 4 23 10 17 10"/><polyline points="1 20 1 14 7 14"/>
            <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15"/>
          </svg>
        </button>
      </div>

      <div className="pe-untersektion">
        <h3 className="pe-untersektion-titel">Abonnement</h3>

        {aboAktiv && sub ? (
          <div className="pe-abr-paket-card">
            <div className="pe-abr-paket-header">
              <div>
                <span className="pe-abr-paket-name">{planName}</span>
                <span className="pe-abr-paket-badge">Aktiv</span>
                {istAdminGrant && <span className="pe-abr-paket-badge pe-abr-paket-badge--admin">Admin</span>}
                {istGekuendigt && <span className="pe-abr-paket-badge pe-abr-paket-badge--gekuendigt">Gekündigt</span>}
              </div>
              {ablaufFormatiert && (
                <span className="pe-abr-paket-ablauf">
                  {istAdminGrant ? 'Gültig bis' : istGekuendigt ? 'Läuft ab am' : 'Verlängert am'} {ablaufFormatiert}
                </span>
              )}
            </div>
            <div className="pe-abr-zusatz-hinweis">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>
              </svg>
              Unbegrenzte Nutzung des Agenten-Workflows
            </div>
            <div className="pe-abr-zusatz-hinweis">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M20 7H4a2 2 0 0 0-2 2v9a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2V9a2 2 0 0 0-2-2z"/><path d="M16 3v4M8 3v4M2 11h20"/>
              </svg>
              Abonniert über {quelleLabel}
            </div>
            {!istAdminGrant && sub?.stripe_subscription_id && !istGekuendigt && (
              <div className="pe-abr-kuendigen-zeile">
                <button className="pe-abr-kuendigen-btn" onClick={onKuendigen}>
                  Abo kündigen
                </button>
              </div>
            )}
            {istAppleAbo && !istGekuendigt && (
              <div className="pe-abr-kuendigen-zeile">
                <a
                  className="pe-abr-kuendigen-btn pe-abr-kuendigen-btn--apple"
                  href="https://apps.apple.com/account/subscriptions"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Bei Apple verwalten / kündigen →
                </a>
              </div>
            )}
          </div>
        ) : (
          <div className="pe-abr-leer">
            <div className="pe-abr-free-kontingent">
              <span className="pe-abr-free-zahl">{freeUebrig}</span>
              <span className="pe-abr-free-label">von {freeLimit} kostenlosen Bewerbungen übrig</span>
            </div>
            <p className="pe-abr-leer-text">Kein aktives Abonnement vorhanden.</p>
            <button className="pe-abr-cta" onClick={onPaketWaehlen}>
              Paket auswählen →
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
